/**
 * Tipos del módulo Finanzas — espejan las respuestas de cyclone `/v1/finanzas/*`
 * (cyclone/src/repos/finanzas.repo.ts + finanzas.controller.ts). Un solo read-model:
 * ninguna vista puede descuadrar contra otra.
 */

// `pp` (tarjeta prepaga) y `nd` (reserva cargada a mano sin medio declarado —
// payments.type = 'vendor_invite') salieron del cajón "otro", que en 90 días de prod
// escondía $45M mezclando plata real con ruido. Ver cyclone finanzas.repo.methodOfPaymentType.
// Las tabs viven acá y no en Finanzas.tsx a propósito: el Router necesita
// FINANZAS_ADDON_TABS para decidir si una ruta es del add-on, y al importarla del
// contenedor se llevaba puesto TODO Finanzas al bundle eager (+814 KB, +44 assets
// en el critical path). Este archivo no importa nada, así que es gratis.
export type FinanzasTab =
  'resumen' | 'ventas' | 'por-cobrar' | 'cobros' | 'gastos' | 'comisiones' | 'resultado' | 'caja';

/** Las cuatro secciones que suma el add-on Finanzas Pro. Sin él llevan al onboarding. */
export const FINANZAS_ADDON_TABS: FinanzasTab[] = ['comisiones', 'gastos', 'resultado', 'caja'];

export type FinanzasMethod = 'ef' | 'tr' | 'mp' | 'cr' | 'de' | 'pp' | 'nd' | 'otro';
export type FinanzasAccount = 'caja' | 'banco' | 'mp';

/** Evita exponer copy de Comisiones fuera del plan venue sin alterar el dato contable. */
export const expenseCategoryLabel = (name: string, hasCommissions: boolean): string =>
  !hasCommissions && name === 'Sueldos y comisiones' ? 'Sueldos' : name;
export type FinanzasAsset =
  | 'servicio'
  | 'producto'
  | 'curso'
  | 'capacitacion'
  | 'descargable'
  | 'giftcard'
  | 'comunidad'
  | 'manual'
  | 'otro';

export interface DistEntryI {
  key: string;
  amount: number;
  /** Movimientos del segmento. Solo lo manda el Resumen; un cobro con productos cuenta en los dos tipos. */
  count?: number;
}

/** Total de una distribución: monto y movimientos distintos (no la suma de los segmentos). */
export interface DistTotalI {
  amount: number;
  count: number;
}

export interface FinanzasSummaryI {
  range: { from: string; to: string };
  /** Medio por el que se filtró el resumen (`null` = todos). Con filtro, los tiles y las
   *  distribuciones hablan sólo de lo que entró y salió por ese medio. */
  method?: FinanzasMethod | null;
  tiles: {
    vendido: number;
    cobrado: number;
    cobros_count: number;
    devoluciones: number;
    devoluciones_count?: number;
    por_cobrar: { total: number; del_periodo: number; count: number };
    // Del add-on: sin contratarlo el backend no los manda (esconderlos sólo en el front
    // dejaría los montos a la vista en la red).
    gastos?: { total: number; count: number };
    neto?: number;
  };
  dist: {
    cobrado: DistGroupI;
    vendido: DistGroupI;
  };
  /** Comisión de Ágora del período — ya NETEADA de cobrado/neto; informativa. */
  agora_fee_total: number;
  usd: UsdInfoI;
  accionables: AccionableI[];
  /** Alguna fuente del período alcanzó el techo defensivo; los totales pueden ser parciales. */
  truncated?: boolean;
}

export interface DistGroupI {
  /** Medio, tipo, concepto y sucursal reparten las mismas filas (`all`); profesional, sólo las suyas. */
  totals?: { all: DistTotalI; by_staff: DistTotalI };
  by_method: DistEntryI[];
  by_asset: DistEntryI[];
  by_concept: DistEntryI[];
  by_staff: DistEntryI[];
  by_venue: DistEntryI[];
}

/** Hechos derivados del ledger. La copy vive en el front; el backend manda datos. */
export type AccionableI =
  | { key: 'por_cobrar'; total: number; count: number; sin_sena_count: number }
  | { key: 'comisiones_sin_liquidar'; total: number; staff_count: number }
  | { key: 'caja_abierta'; opened_at: string }
  | { key: 'arqueo_con_diferencia'; date: string; difference: number; reason: string | null }
  | { key: 'resultado_negativo'; neto: number }
  | { key: 'gasto_categoria_salto'; category: string; before: number; now: number; pct: number }
  | { key: 'efectivo_caja_cerrada'; total: number };

export interface VentaRowI {
  id: string;
  kind: 'venta' | 'devolucion';
  booking_id: number | null;
  /** Alta contable sintética de Finanzas, sin detalle operativo de reserva. */
  manual_finanzas: boolean;
  payment_id: number | null;
  date: string;
  asset: FinanzasAsset | 'refund';
  booking_type: string | null;
  concept: string;
  item_count: number;
  client_name: string | null;
  staff_vendor_id: number | null;
  staff_name: string | null;
  venue_id: number | null;
  venue_name: string | null;
  method: FinanzasMethod;
  account: FinanzasAccount;
  /** Lo que RECIBE el negocio (neto de agora_fee) o el monto devuelto. */
  amount: number;
  /** Comisión de Ágora del pago; 0 si no aplica. amount + agora_fee + pending == total. */
  agora_fee: number;
  total_amount: number;
  pending: number;
  /** Excedente cobrado tras bajar el total de una venta; Cobros conserva la plata real. */
  over_collected?: number;
  /** Parte de la venta que es producto (0..1): un turno con productos es UN solo pago. */
  product_share: number;
}

/** Ventas en USD del período — SIEMPRE aparte, nunca sumadas a los pesos. */
export interface UsdInfoI {
  count: number;
  amount: number;
}

export interface VentasResponseI {
  data: VentaRowI[];
  total: number;
  totals: { cobrado: number; devuelto: number; pendiente: number };
  usd: UsdInfoI;
  truncated?: boolean;
  /** Tipos presentes en las filas del período, antes de filtrar (opciones del filtro "Tipo"). */
  assets_present?: string[];
}

export interface CobroRowI {
  id: string;
  date: string;
  concept: string;
  client_name: string | null;
  asset: FinanzasAsset;
  staff_vendor_id: number | null;
  staff_name: string | null;
  venue_id: number | null;
  venue_name: string | null;
  method: FinanzasMethod;
  account: FinanzasAccount;
  source: 'auto' | 'manual';
  /** QUÉ se cobró: seña, saldo, el pago entero de una vez, un ingreso suelto o un ajuste. */
  leg: FinanzasLeg;
  amount: number;
  /** true = seña sin fecha propia: fechada al alta del pago (se muestra con ≈). */
  date_approx?: boolean;
  booking_id: number | null;
  payment_id: number | null;
  /** Parte del cobro que es producto (0..1). */
  product_share: number;
}

export interface CobrosResponseI {
  data: CobroRowI[];
  total: number;
  total_amount: number;
  refunds_total: number;
  manual: { count: number; total: number };
  usd: UsdInfoI;
  truncated?: boolean;
  /** Tipos presentes en las filas del período, antes de filtrar (opciones del filtro "Tipo"). */
  assets_present?: string[];
}

export interface FinanzasReceivableI {
  booking_id: number;
  payment_id: number;
  client_name: string | null;
  client_phone: string | null;
  asset: 'servicio' | 'producto';
  concept: string;
  staff_vendor_id: number | null;
  staff_name: string | null;
  total_amount: number;
  down_payment_amount: number;
  collected: number;
  pending: number;
  sale_date: string | null;
  turno_date: string | null;
  /**
   * Trabajo YA entregado vs turno todavía por venir. Lo resuelve el backend con la
   * zona del negocio: repetir el corte acá daría otro resultado cerca de medianoche.
   */
  estado: 'entregado' | 'futuro';
  /** Parte del saldo que es producto (0..1). */
  product_share: number;
}

export interface PorCobrarResponseI {
  data: FinanzasReceivableI[];
  total: number;
  total_pending: number;
  con_sena: { count: number; total: number };
  trabajos_realizados: { count: number; total: number };
  turnos_futuros: { count: number; total: number };
  truncated?: boolean;
}

export interface GastoRowI {
  id: number;
  date: string;
  type: 'expense' | 'payout';
  concept: string;
  category: string | null;
  category_id: number | null;
  method: FinanzasMethod;
  account: FinanzasAccount;
  amount: number;
  staff_vendor_id: number | null;
  receipt_url: string | null;
}

export interface GastosResponseI {
  data: GastoRowI[];
  total: number;
  total_amount: number;
  by_category: DistEntryI[];
  by_account: DistEntryI[];
  truncated?: boolean;
}

export interface ComisionesCurvaPointI {
  /** Día (YYYY-MM-DD). */
  date: string;
  /** Saldo pendiente de liquidar al cierre del día. */
  saldo: number;
  /** Total liquidado (pagado al equipo) ese día; 0 si no hubo pagos. */
  liquidado: number;
}

export interface ComisionesCurvaResponseI {
  points: ComisionesCurvaPointI[];
  liquidaciones: { count: number; total: number };
}

export interface ResultadoResponseI {
  range: { from: string; to: string };
  cascade: {
    cobrado: number;
    devoluciones: number;
    egresos: number;
    gastos: number;
    comisiones_liquidadas: number;
    neto: number;
  };
  agora_fee_total: number;
  usd: UsdInfoI;
  comisiones_sin_liquidar: number;
  de_donde_vino: DistEntryI[];
  en_que_se_fue: DistEntryI[];
  /** Premios: lo descontado en premios en el período. null (o ausente) si no hubo. No cambia el neto. */
  premios?: ResultadoPremiosI | null;
}

export interface ResultadoPremiosItemI {
  title: string;
  /** null sin permiso para ver nombres. */
  client_name: string | null;
  at: string | null;
  kind: 'canje' | 'alcanzado';
  amount: number;
  /** Canje: 1 por uso. Alcanzado: los turnos con ese descuento. */
  count: number;
}

export interface ResultadoPremiosI {
  total: number;
  canjes: number;
  alcanzados: number;
  items: ResultadoPremiosItemI[];
}

// ── Labels compartidos ──────────────────────────────────────────────────────────

export const METHOD_LABELS: Record<FinanzasMethod | 'pend', string> = {
  ef: 'Efectivo',
  tr: 'Transferencia',
  mp: 'Mercado Pago',
  cr: 'Tarjeta crédito',
  de: 'Tarjeta débito',
  pp: 'Tarjeta prepaga',
  nd: 'Sin especificar',
  otro: 'Otro medio',
  pend: 'Sin cobrar'
};

/**
 * Cuenta destino/origen, versión CORTA — es una celda de tabla al lado de otras ocho,
 * y "Billetera Mercado Pago" sola empujaba la columna Monto fuera de pantalla.
 * La versión larga vive en la card de distribución de Gastos.
 */
export const ACCOUNT_LABELS: Record<FinanzasAccount, string> = {
  caja: 'Caja',
  banco: 'Tu cuenta',
  mp: 'Billetera'
};

/**
 * Concepto del cobro: QUÉ se cobró, distinto del tipo de venta (de qué fue). Un mismo
 * servicio puede entrar como seña primero y como saldo después.
 */
export type FinanzasLeg = 'sena' | 'saldo' | 'total' | 'manual' | 'ajuste';

export const LEG_LABELS: Record<FinanzasLeg, string> = {
  sena: 'Seña',
  saldo: 'Saldo',
  total: 'Pago total',
  manual: 'Ingreso manual',
  ajuste: 'Ajuste'
};

export const ASSET_LABELS: Record<string, string> = {
  servicio: 'Servicios',
  producto: 'Productos',
  curso: 'Cursos online',
  capacitacion: 'Capacitaciones',
  descargable: 'Descargables',
  giftcard: 'Gift cards',
  comunidad: 'Comunidad',
  manual: 'Ingresos manuales',
  refund: 'Devoluciones',
  otro: 'Otros'
};

/**
 * Rampa de UN solo tono por medio, con paso fijo (decisión del proto): con la paleta
 * de marca acotada no existe un quinteto categórico que separe, así que el color deja
 * de identificar — lo hace la etiqueta, siempre al lado. El efectivo queda en verde
 * porque no es una categoría más: es el único que se cuenta a mano y entra al arqueo.
 */
export const METHOD_COLORS: Record<FinanzasMethod | 'pend', string> = {
  ef: 'var(--f-success)',
  tr: 'var(--f-brand-deep)',
  mp: 'var(--f-brand)',
  cr: 'var(--f-brand-bright)',
  de: 'rgba(var(--f-brand-rgb), 0.55)',
  pp: 'rgba(var(--f-brand-rgb), 0.32)',
  nd: 'var(--f-hair-stronger)',
  otro: 'var(--f-ink-whisper)',
  pend: 'var(--f-hair-stronger)'
};
